import { useState } from "react";
import Reveal from "@/components/Reveal";
import { MapPin, Phone, Clock, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";

const FORMSPREE_ENDPOINT = "https://formspree.io/f/mykrqdrn";

const INFO = [
  {
    icon: MapPin,
    label: "Atelier",
    value: "Selçuk Bey, 750. Sk. No: 9/5, 20030 Merkezefendi / Denizli, Türkiye",
    href: "https://www.google.com/maps/search/?api=1&query=Sel%C3%A7uk+Bey+750.+Sk.+No%3A9%2F5+Merkezefendi+Denizli",
    testid: "contact-address",
  },
  {
    icon: Phone,
    label: "Telephone",
    value: "+90 533 552 02 93",
    href: "tel:+905335520293",
    testid: "contact-phone",
  },
  {
    icon: Clock,
    label: "Hours",
    value: "Opens Monday 08:30",
    href: null,
    testid: "contact-hours",
  },
];

const EMPTY = { name: "", company: "", email: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          name: form.name,
          company: form.company || "—",
          email: form.email,
          message: form.message,
          _subject: "Nevatextile — New website enquiry",
        }),
      });
      if (!res.ok) throw new Error("failed");
      toast.success("Enquiry sent — we will reply within one working day.");
      setForm(EMPTY);
    } catch {
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 sm:py-32 border-t border-line">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="text-[11px] uppercase tracking-[0.3em] font-semibold text-clay mb-6">
              05 — Contact
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif font-light tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl">
              Let's weave
              <em className="italic text-clay"> something.</em>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 text-smoke text-base sm:text-lg leading-relaxed max-w-md">
              Samples, quotations and private-label programmes — write to us or
              visit the atelier in Denizli.
            </p>
          </Reveal>

          <div className="mt-12 space-y-8">
            {INFO.map((item, i) => (
              <Reveal key={item.label} delay={0.1 + i * 0.08}>
                <div className="flex gap-5 items-start" data-testid={item.testid}>
                  <span className="w-10 h-10 border border-line flex items-center justify-center shrink-0 text-clay">
                    <item.icon size={16} />
                  </span>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.3em] text-smoke mb-1">{item.label}</p>
                    {item.href ? (
                      <a
                        href={item.href}
                        target={item.href.startsWith("http") ? "_blank" : undefined}
                        rel="noopener noreferrer"
                        className="text-base sm:text-lg hover:text-clay transition-colors duration-300 underline-offset-4 hover:underline"
                      >
                        {item.value}
                      </a>
                    ) : (
                      <p className="text-base sm:text-lg">{item.value}</p>
                    )}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.15}>
            <form
              data-testid="contact-form"
              onSubmit={submit}
              className="bg-white border border-line p-8 sm:p-12 shadow-[0_8px_32px_rgba(0,0,0,0.04)]"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-10">
                <label className="block">
                  <span className="text-[10px] uppercase tracking-[0.3em] text-smoke">Name *</span>
                  <input
                    data-testid="contact-form-name-input"
                    required
                    value={form.name}
                    onChange={set("name")}
                    className="mt-2 w-full bg-transparent border-b border-line focus:border-ink py-3 text-base transition-colors duration-300"
                    placeholder="Your full name"
                  />
                </label>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-[0.3em] text-smoke">Company</span>
                  <input
                    data-testid="contact-form-company-input"
                    value={form.company}
                    onChange={set("company")}
                    className="mt-2 w-full bg-transparent border-b border-line focus:border-ink py-3 text-base transition-colors duration-300"
                    placeholder="Brand or hotel"
                  />
                </label>
                <label className="block sm:col-span-2">
                  <span className="text-[10px] uppercase tracking-[0.3em] text-smoke">Email *</span>
                  <input
                    data-testid="contact-form-email-input"
                    type="email"
                    required
                    value={form.email}
                    onChange={set("email")}
                    className="mt-2 w-full bg-transparent border-b border-line focus:border-ink py-3 text-base transition-colors duration-300"
                    placeholder="you@company.com"
                  />
                </label>
                <label className="block sm:col-span-2">
                  <span className="text-[10px] uppercase tracking-[0.3em] text-smoke">Message *</span>
                  <textarea
                    data-testid="contact-form-message-input"
                    required
                    rows={4}
                    value={form.message}
                    onChange={set("message")}
                    className="mt-2 w-full bg-transparent border-b border-line focus:border-ink py-3 text-base transition-colors duration-300 resize-none"
                    placeholder="Quantities, target fabrics, timelines..."
                  />
                </label>
              </div>
              <input
                type="text"
                name="_gotcha"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                style={{ position: "absolute", left: "-9999px" }}
              />
              <button
                data-testid="contact-form-submit-button"
                type="submit"
                disabled={sending}
                className="group mt-12 inline-flex items-center gap-3 bg-ink text-cream px-8 py-4 text-[11px] uppercase tracking-[0.25em] font-semibold hover:bg-clay transition-colors duration-300 disabled:opacity-60"
              >
                {sending ? (
                  <>
                    Sending <Loader2 size={14} className="animate-spin" />
                  </>
                ) : (
                  <>
                    Send enquiry
                    <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </>
                )}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
